<script setup lang="ts">
// The environment's database (docs/proposal-db-console.md): GET /environments/{name}/db lists the application
// schemas, POST …/db/query runs one statement. The API decides who may read and write, checks the statement and runs
// it as the matching account; this offers a schema browser, an editor (Ctrl/⌘+Enter runs), the result grid, CSV
// download and a per-browser history. A write (INSERT, UPDATE, DELETE, REPLACE; operators only) is sent as a preview
// first (run and rolled back, it reports the rows it matches) and committed only after the confirmation.
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { must } from '../api/client';
import type { DbResult, DbSchema } from '../api/types';
import { useContext } from '../context';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';
import { compact, describeQuery, firstKeyword, isWrite, loadHistory, remember, tableQuery, toCsv, type HistoryEntry } from '../db/console';
import { confirmationMatches } from '../actions/guards';

const props = defineProps<{ environment: string; raw?: boolean; source?: string; canWrite?: boolean }>();
const { api } = useContext();

const schema = ref<DbSchema>();
const schemaError = ref<unknown>();
const loadingSchema = ref(false);
const filter = ref('');
const open = ref<Set<string>>(new Set());

const database = ref<string>('');
const sql = ref('');
const limit = ref(200);
const LIMITS = [50, 200, 1000] as const;

const result = ref<DbResult>();
const ran = ref<HistoryEntry>();
const error = ref<unknown>();
const running = ref(false);
const history = ref<HistoryEntry[]>(loadHistory(props.environment));
const editor = ref<HTMLTextAreaElement>();

async function loadSchema() {
  loadingSchema.value = true;
  try {
    const s = must(await api.GET('/environments/{name}/db', { params: { path: { name: props.environment } } }));
    schema.value = s;
    schemaError.value = undefined;
    if (!database.value || !s.databases.some((d) => d.name === database.value)) {
      database.value = s.databases.find((d) => d.name === 'shop')?.name ?? s.databases[0]?.name ?? '';
    }
    if (open.value.size === 0 && database.value) open.value = new Set([database.value]);
  } catch (e) {
    schemaError.value = e;
  } finally {
    loadingSchema.value = false;
  }
}

async function send(entry: HistoryEntry, write?: 'preview' | 'commit'): Promise<DbResult> {
  return must(await api.POST('/environments/{name}/db/query', {
    params: { path: { name: props.environment } },
    body: { database: entry.database, sql: entry.sql, limit: limit.value, ...(write ? { write } : {}) },
  }));
}

async function run() {
  const text = sql.value.trim();
  if (!text || running.value) return;
  const entry: HistoryEntry = { database: database.value || null, sql: text };
  if (isWrite(text) && !props.canWrite) {
    error.value = new Error(`${firstKeyword(text)} changes rows: writing needs the operator role on ${props.environment}.`);
    return;
  }
  running.value = true;
  try {
    if (isWrite(text)) {
      const p = await send(entry, 'preview');
      pending.value = { entry, preview: p };
      typed.value = '';
    } else {
      result.value = await send(entry);
      ran.value = entry;
    }
    error.value = undefined;
  } catch (e) {
    error.value = e;
  } finally {
    running.value = false;
    history.value = remember(props.environment, entry);
  }
}

// ---- writes: preview → confirm → commit
const pending = ref<{ entry: HistoryEntry; preview: DbResult }>();
const typed = ref('');
const dlg = ref<HTMLDialogElement>();
const mayCommit = computed(() => !!pending.value && (!props.raw || confirmationMatches(typed.value, props.environment)));
watch(pending, async (p) => {
  await nextTick();
  if (!dlg.value) return;
  if (p && !dlg.value.open) dlg.value.showModal?.() ?? dlg.value.setAttribute('open', '');
  else if (!p && dlg.value.open) dlg.value.close?.() ?? dlg.value.removeAttribute('open');
});

async function commit() {
  const p = pending.value;
  if (!p || !mayCommit.value || running.value) return;
  running.value = true;
  try {
    result.value = await send(p.entry, 'commit');
    ran.value = p.entry;
    error.value = undefined;
    pending.value = undefined;
  } catch (e) {
    error.value = e;
    pending.value = undefined;
  } finally {
    running.value = false;
  }
}

const rowsText = (n: number) => `${n.toLocaleString('en')} ${n === 1 ? 'row' : 'rows'}`;

function pick(db: string, statement: string) {
  database.value = db;
  sql.value = statement;
  void run();
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    void run();
  }
}

function useHistory(ev: Event) {
  const i = Number((ev.target as HTMLSelectElement).value);
  const h = history.value[i];
  (ev.target as HTMLSelectElement).value = '';
  if (!h) return;
  if (h.database) database.value = h.database;
  sql.value = h.sql;
  editor.value?.focus();
}

function toggle(db: string) {
  const next = new Set(open.value);
  if (next.has(db)) next.delete(db); else next.add(db);
  open.value = next;
}

const shown = computed(() => {
  const f = filter.value.trim().toLowerCase();
  return (schema.value?.databases ?? []).map((d) => ({
    ...d,
    tables: f ? d.tables.filter((t) => t.name.toLowerCase().includes(f)) : d.tables,
  })).filter((d) => !f || d.tables.length || d.name.toLowerCase().includes(f));
});

function download() {
  const r = result.value;
  if (!r) return;
  const blob = new Blob([toCsv(r)], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${props.environment}-${r.database ?? 'db'}-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const short = (s: string) => (s.length > 90 ? s.slice(0, 90) + '…' : s).replace(/\s+/g, ' ');

onMounted(loadSchema);
</script>

<template>
  <div class="dbc">
    <p v-if="raw" class="alert banner" role="note">
      <Icon name="lock" :size="18" />
      <span class="grow"><strong>Raw production data.</strong> This database may hold customer data: you see it because you are on the access list
        and entitled to <code>{{ source }}</code>. Every statement is audited.</span>
    </p>

    <div class="cols">
      <aside class="card dbc-schema" aria-labelledby="dbc-schema-h">
        <div class="dbc-schema-head">
          <h2 id="dbc-schema-h">Schemas</h2>
          <button type="button" class="link end" :disabled="loadingSchema" @click="loadSchema">{{ loadingSchema ? 'loading…' : 'Reload' }}</button>
        </div>
        <label class="sr-only" for="dbc-filter">Find a table</label>
        <input id="dbc-filter" v-model="filter" type="search" placeholder="Find a table" class="dbc-filter" data-testid="db-filter" />
        <ProblemAlert :error="schemaError" context="Schemas" />
        <ul class="dbc-tree" data-testid="db-schema">
          <li v-for="d in shown" :key="d.name">
            <button type="button" class="dbc-db" :aria-expanded="open.has(d.name) || !!filter.trim()" @click="toggle(d.name)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"
                :style="open.has(d.name) || filter.trim() ? 'transform: rotate(90deg)' : ''"><path d="M9 6l6 6-6 6" /></svg>
              <span class="mono">{{ d.name }}</span><span class="n">{{ d.tables.length }}</span>
            </button>
            <ul v-if="open.has(d.name) || filter.trim()">
              <li v-for="t in d.tables" :key="t.name" class="dbc-table">
                <button type="button" class="dbc-t mono" :title="`SELECT * FROM ${t.name}`" :data-testid="`db-table-${d.name}-${t.name}`" @click="pick(d.name, tableQuery(t.name))">
                  {{ t.name }}<span v-if="t.type === 'VIEW'" class="tag">view</span>
                </button>
                <span class="n" :title="t.bytes != null ? compact(t.bytes, 'bytes') : undefined">{{ compact(t.rows, 'rows') }}</span>
                <button type="button" class="dbc-desc" :aria-label="`Describe ${t.name}`" title="Columns" @click="pick(d.name, describeQuery(t.name))">cols</button>
              </li>
              <li v-if="!d.tables.length" class="hint">No table.</li>
            </ul>
          </li>
          <li v-if="schema && !shown.length" class="hint">No schema or table matches.</li>
        </ul>
        <p class="hint">Row counts are the engine's estimates. Click a table to see its first rows.</p>
      </aside>

      <section class="card dbc-main" aria-labelledby="dbc-h">
        <div class="dbc-bar">
          <h2 id="dbc-h">Query</h2>
          <label class="dbc-inline">Schema
            <select v-model="database" data-testid="db-database">
              <option value="">(none)</option>
              <option v-for="d in schema?.databases ?? []" :key="d.name" :value="d.name">{{ d.name }}</option>
            </select>
          </label>
          <div class="seg" role="radiogroup" aria-label="Rows">
            <label v-for="l in LIMITS" :key="l"><input v-model="limit" type="radio" name="db-limit" :value="l" />{{ l }} rows</label>
          </div>
          <select v-if="history.length" class="dbc-history" aria-label="Recent statements" data-testid="db-history" @change="useHistory">
            <option value="">Recent…</option>
            <option v-for="(h, i) in history" :key="i" :value="i">{{ h.database ? h.database + ': ' : '' }}{{ short(h.sql) }}</option>
          </select>
        </div>
        <label class="sr-only" for="dbc-sql">SQL statement</label>
        <textarea id="dbc-sql" ref="editor" v-model="sql" class="dbc-sql" spellcheck="false" maxlength="20000" rows="6"
          placeholder="SELECT path, value FROM core_config_data WHERE path LIKE 'web/%'" data-testid="db-sql" @keydown="onKey"></textarea>
        <div class="dbc-run">
          <button type="button" class="button primary" :disabled="running || !sql.trim()" data-testid="db-run" @click="run">{{ running ? 'Running…' : 'Run' }}</button>
          <span class="hint">Ctrl/⌘ + Enter · one statement · 30 s limit · every statement is audited ·
            <template v-if="canWrite">INSERT, UPDATE, DELETE, REPLACE are previewed first and committed after you confirm</template>
            <template v-else>read-only for you (writing needs the operator role)</template></span>
        </div>

        <ProblemAlert :error="error" context="Query" />

        <p v-if="result?.write" class="alert banner dbc-written" role="status" data-testid="db-committed">
          <Icon name="check" :size="18" />
          <span class="grow"><strong>Committed:</strong> {{ firstKeyword(ran?.sql ?? '') }} changed {{ rowsText(result.write.rows) }}<template v-if="ran?.database"> in {{ ran.database }}</template>
            ({{ result.elapsedMs }} ms).<template v-if="result.write.warnings.length"> Warnings: {{ result.write.warnings.join('; ') }}</template></span>
        </p>
        <template v-else-if="result">
          <div class="dbc-head" data-testid="db-summary">
            <strong>{{ result.rows.length.toLocaleString('en') }} {{ result.rows.length === 1 ? 'row' : 'rows' }}</strong>
            <span class="muted">{{ result.elapsedMs }} ms<template v-if="ran?.database"> · {{ ran.database }}</template></span>
            <span v-if="result.more" class="tag tone-warn" data-testid="db-more">more rows than shown</span>
            <span v-if="result.clipped" class="tag tone-warn" title="Cells over 4000 characters (512 bytes of binary) are cut">cells cut</span>
            <button type="button" class="button sm end" :disabled="!result.columns.length" @click="download">Download CSV</button>
          </div>
          <div v-if="result.columns.length" class="dbc-scroll">
            <table class="dbc-grid" data-testid="db-result">
              <thead><tr><th class="ix">#</th><th v-for="(c, i) in result.columns" :key="i" :title="c.type">{{ c.name }}</th></tr></thead>
              <tbody>
                <tr v-for="(row, r) in result.rows" :key="r">
                  <td class="ix">{{ r + 1 }}</td>
                  <td v-for="(v, i) in row" :key="i" :title="v ?? 'NULL'"><span v-if="v === null" class="null">NULL</span><template v-else>{{ v }}</template></td>
                </tr>
              </tbody>
            </table>
            <p v-if="!result.rows.length" class="empty">No row.</p>
          </div>
          <p v-else class="empty">The statement returned no result set.</p>
        </template>
      </section>
    </div>

    <dialog ref="dlg" aria-labelledby="dbc-confirm-h" data-testid="db-confirm" @cancel.prevent="pending = undefined">
      <form v-if="pending?.preview.write" method="dialog" @submit.prevent="commit">
        <h2 id="dbc-confirm-h">Commit this {{ firstKeyword(pending.entry.sql) }}?</h2>
        <p>On <strong>{{ environment }}</strong><template v-if="pending.entry.database">, schema <code>{{ pending.entry.database }}</code></template>,
          it matches <strong data-testid="db-preview-rows">{{ rowsText(pending.preview.write.rows) }}</strong> right now (previewed and rolled back).
          The next refresh-data replaces the data<template v-if="pending.entry.database === 'shop'">, unless the environment preserves its core_config_data</template>.</p>
        <pre class="dbc-stmt">{{ pending.entry.sql }}</pre>
        <p v-if="pending.preview.write.warnings.length" class="alert">Warnings: {{ pending.preview.write.warnings.join('; ') }}</p>
        <div v-if="raw" class="field">
          <label for="dbc-confirm-name">Raw production data: type <code>{{ environment }}</code> to confirm</label>
          <input id="dbc-confirm-name" v-model="typed" autocomplete="off" spellcheck="false" data-testid="db-confirm-name" />
        </div>
        <div class="actions-row">
          <button type="submit" class="danger" :disabled="!mayCommit || running" data-testid="db-commit">{{ running ? 'Committing…' : `Commit ${rowsText(pending.preview.write.rows)}` }}</button>
          <button type="button" data-testid="db-cancel" @click="pending = undefined">Cancel</button>
        </div>
      </form>
    </dialog>
  </div>
</template>

<style scoped>
.dbc { display: flex; flex-direction: column; gap: 1.25rem; }
.dbc-schema { flex: 1 1 260px; min-width: 0; display: flex; flex-direction: column; gap: 0.6rem; max-height: 80vh; overflow: auto; }
.dbc-schema-head { display: flex; align-items: baseline; gap: 8px; }
.dbc-schema-head h2 { margin: 0; font-size: 17px; }
.dbc-schema-head .end { margin-left: auto; }
.dbc-filter { min-height: 30px; font-size: 13px; width: 100%; box-sizing: border-box; }
.dbc-tree, .dbc-tree ul { list-style: none; margin: 0; padding: 0; }
.dbc-tree ul { padding-left: 18px; }
.dbc-db { all: unset; cursor: pointer; display: flex; align-items: center; gap: 6px; width: 100%; min-height: 30px; font-size: 13.5px; font-weight: 600; box-sizing: border-box; }
.dbc-db:focus-visible, .dbc-t:focus-visible, .dbc-desc:focus-visible { outline: 2px solid var(--focus); }
.dbc-db svg { color: var(--muted); flex: none; }
.dbc-table { display: flex; align-items: center; gap: 6px; min-height: 26px; }
.dbc-t { all: unset; cursor: pointer; flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; }
.dbc-t:hover { color: var(--accent); }
.dbc-desc { all: unset; cursor: pointer; font-size: 11px; color: var(--muted); padding: 0 4px; border-radius: 4px; }
.dbc-desc:hover { color: var(--accent); background: var(--idle-bg); }
.n { margin-left: auto; color: var(--muted); font-size: 12px; font-variant-numeric: tabular-nums; flex: none; }
.dbc-main { flex: 999 1 640px; min-width: 0; display: flex; flex-direction: column; gap: 0.75rem; }
.dbc-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem 0.9rem; }
.dbc-bar h2 { margin: 0; font-size: 17px; }
.dbc-inline { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 13px; font-weight: 500; }
.dbc-inline select, .dbc-history { min-height: 32px; font-size: 13px; }
.dbc-history { max-width: 22rem; margin-left: auto; }
.dbc-sql { width: 100%; box-sizing: border-box; min-height: 8rem; font-size: 13px; line-height: 1.5; tab-size: 2; }
.dbc-run { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
.dbc-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 12px; font-size: 13px; }
.dbc-head .end { margin-left: auto; }
.tag { display: inline-block; padding: 0 6px; border-radius: 4px; background: var(--idle-bg); font-size: 11.5px; line-height: 20px; margin-left: 4px; }
.dbc-scroll { overflow: auto; max-height: 65vh; border: 1px solid var(--line); border-radius: 8px; }
.dbc-grid { border-collapse: collapse; font-family: var(--mono); font-size: 12.5px; min-width: 100%; }
.dbc-grid th, .dbc-grid td { padding: 4px 10px; border-bottom: 1px solid var(--line-soft, #eef0f3); text-align: left; vertical-align: top; max-width: 32rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dbc-grid th { position: sticky; top: 0; background: var(--panel, #fafbfc); font-family: inherit; font-weight: 600; text-transform: none; letter-spacing: 0; color: var(--fg); z-index: 1; }
.dbc-grid tr:hover td { background: var(--bg); }
.dbc-grid .ix { color: var(--muted); text-align: right; font-variant-numeric: tabular-nums; }
.null { color: var(--muted); font-style: italic; }
.empty { padding: 24px 16px; text-align: center; color: var(--muted); margin: 0; }
.dbc-stmt { margin: 0; padding: 10px 12px; background: var(--bg); border: 1px solid var(--line); border-radius: 8px; font-family: var(--mono); font-size: 12.5px; white-space: pre-wrap; overflow-wrap: anywhere; max-height: 14rem; overflow: auto; }
</style>
